import { collection, addDoc, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { readMeta, saveMeta } from './privateMeta';
import { displayName } from './names';

// Invite a study partner (brief 035, P-1). One reusable code per user, stored in
// invites/{code} (readable by its code, never listable) and in the inviter's private
// meta. Whoever signs up through the link is connected to the inviter with a first
// message once their profile is set up. No rewards, points or counts.

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz23456789';
export const newInviteCode = (random = Math.random) =>
  Array.from({ length: 10 }, () => ALPHABET[Math.floor(random() * ALPHABET.length)]).join('');
export const isInviteCode = v => /^[a-z2-9]{10}$/.test(v || '');
export const inviteUrl = code => `https://medxmeet.com/?invite=${code}`;
export const firstName = name => displayName(name, '').split(/\s+/)[0] || 'A study partner';

export const INVITE_TEXT = url =>
  `I'm using MedXMeet to find USMLE study partners. It's free, with no third-party ads. Join with my link and we'll be connected right away: ${url}`;

// The signed-in user's invite link, creating the code on first use.
export async function myInviteUrl(uid, profile) {
  const meta = await readMeta(uid);
  if (isInviteCode(meta.inviteCode)) return inviteUrl(meta.inviteCode);
  const code = newInviteCode();
  await setDoc(doc(db, 'invites', code), {
    inviterUid: uid,
    inviterFirstName: firstName(profile?.name),
    category: profile?.studyCategory || '',
    createdAt: serverTimestamp(),
  });
  await saveMeta(uid, { inviteCode: code });
  return inviteUrl(code);
}

// The invite behind a code, or null (unknown codes are ignored silently).
export async function readInvite(code) {
  if (!isInviteCode(code)) return null;
  try {
    const snap = await getDoc(doc(db, 'invites', code));
    return snap.exists() ? snap.data() : null;
  } catch { return null; }
}

export const JOINED_TEXT = 'Hi! I joined MedXMeet from your invite.';

// Should this person be connected to their inviter now? Once only, never to themselves.
export const shouldConnect = (meta, invite, uid) =>
  !!(meta?.invitedBy && !meta.inviteConnected && invite?.inviterUid && invite.inviterUid !== uid);

// After setup: the usual two message copies to the inviter, an invite_joined
// notification (if they get message notifications), then mark it done.
export async function connectInvite(uid, myName) {
  const meta = await readMeta(uid);
  const invite = meta.invitedBy ? await readInvite(meta.invitedBy) : null;
  if (!shouldConnect(meta, invite, uid)) return false;
  const to = invite.inviterUid;
  const toName = invite.inviterFirstName || 'Study partner';
  const line = { fromUid: uid, fromName: myName, toUid: to, toName, content: JOINED_TEXT, createdAt: serverTimestamp() };
  await addDoc(collection(db, 'conversations'), { ...line, participants: [uid, to] });
  await addDoc(collection(db, 'messages'), { ...line, read: false });
  try {
    const inviter = await getDoc(doc(db, 'users', to));
    if (inviter.exists() && inviter.data()?.notificationPreferences?.newMessages !== false) {
      await addDoc(collection(db, 'notifications'), {
        toUid: to, fromUid: uid, fromName: myName,
        type: 'invite_joined', text: `${myName} joined MedXMeet from your invite`,
        read: false, createdAt: serverTimestamp(),
      });
    }
  } catch (e) { console.warn('invite notification failed', e); }
  await saveMeta(uid, { inviteConnected: true });
  return true;
}
